import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AccessTokenService } from 'src/app/Services/access-token.service';
import { PingSsoService, SsoProviderDescriptor } from 'src/app/Services/ping-sso.service';
import { SettingsService } from 'src/app/Services/settings.service';
import { UserDataProviderService } from 'src/app/Services/user-data-provider.service';
import { AnalyticsService } from 'src/app/Services/analytics.service';

@Component({
  selector: 'app-ping-callback-handler',
  standalone: true,
  template: '',
})
export class PingCallbackHandlerComponent implements OnInit {
  constructor(
    private settingsService: SettingsService,
    private pingSso: PingSsoService,
    private accessTokenService: AccessTokenService,
    private userDataProviderService: UserDataProviderService,
    private router: Router,
    private analytics: AnalyticsService,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      const descriptor = await this.resolvePingProvider();
      if (!descriptor) {
        this.router.navigateByUrl('/login?error=UNAUTHORIZED_SSO_LOGIN');
        return;
      }
      this.pingSso.configure(descriptor);

      // Pull the {code, codeVerifier, redirectUri} out of the URL + sessionStorage.
      // The actual code-for-tokens exchange happens server-side so the client_secret
      // never reaches the browser.
      const callback = this.pingSso.completeSignIn();
      this.accessTokenService
        .exchangePingCodeAndProceed(callback)
        .subscribe(
          (response: any) => {
            if (response.body?.status === 'success' && response.status === 200) {
              this.accessTokenService.processLogin(response);
              this.analytics.setUser(this.accessTokenService.getUserId(), this.accessTokenService.getUserRole());
              this.analytics.trackLogin('sso-ping');
              this.userDataProviderService.isShowSidebar.emit(true);
            } else {
              this.analytics.trackLoginFailed('sso-ping', 'exchange_rejected');
              this.router.navigateByUrl('/login?error=UNAUTHORIZED_SSO_LOGIN');
            }
          },
          (err: any) => {
            // The observable's error path is delivered asynchronously, so the
            // surrounding try/catch can't see it — handle it here or the user
            // is stranded on the blank callback page.
            console.error('[PingCallback] backend code-exchange failed', err);
            this.analytics.trackLoginFailed('sso-ping', 'exchange_error');
            this.router.navigateByUrl('/login?error=UNAUTHORIZED_SSO_LOGIN');
          },
        );
    } catch (err) {
      console.error('[PingCallback] code exchange failed', err);
      this.router.navigateByUrl('/login?error=UNAUTHORIZED_SSO_LOGIN');
    }
  }

  private async resolvePingProvider(): Promise<SsoProviderDescriptor | null> {
    return new Promise((resolve) => {
      this.settingsService.getSsoProviders().subscribe({
        next: (response: any) => {
          if (response?.status !== 'success' || !Array.isArray(response.data)) {
            resolve(null);
            return;
          }
          const ping = response.data.find(
            (p: SsoProviderDescriptor) => p.type?.toLowerCase() === 'ping',
          );
          resolve(ping ?? null);
        },
        error: () => resolve(null),
      });
    });
  }
}
